import { z } from 'zod';

export const FIELD_VALIDATION_CONSTANTS = {
  MAX_LABEL_LENGTH: 50,
  MAX_OPTIONS_COUNT: 100,
} as const;

const baseDataFieldSchema = z.object({
  existingFieldIds: z.array(z.string()).optional(),
  currentFieldId: z.string().optional(),
  id: z
    .string()
    .min(1, { message: 'ID is required' })
    .regex(/^[a-z0-9_]+$/i, {
      message: 'ID can only contain letters, numbers, and underscores',
    }),
  label: z
    .string()
    .min(1, { message: 'Label is required' })
    .max(FIELD_VALIDATION_CONSTANTS.MAX_LABEL_LENGTH, {
      message: `Label must be ${FIELD_VALIDATION_CONSTANTS.MAX_LABEL_LENGTH} characters or less`,
    }),
});

export const stringDataFieldSchema = baseDataFieldSchema
  .extend({
    type: z.literal('string'),
    default_value: z.string().nullable().optional(),
    options: z.array(z.string()).nullable().optional(),
  })
  .refine(
    (data) =>
      !data.existingFieldIds ||
      !data.existingFieldIds.includes(data.id) ||
      data.id === data.currentFieldId,
    {
      path: ['id'],
      message: 'ID must be unique',
    }
  )
  .refine(
    (data) =>
      !data.options ||
      data.options.length <= FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT,
    {
      path: ['options'],
      message: `Maximum ${FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT} options allowed`,
    }
  );

export const integerDataFieldSchema = baseDataFieldSchema
  .extend({
    type: z.literal('integer'),
    default_value: z.number().int().nullable().optional(),
    options: z.array(z.number().int()).nullable().optional(),
  })
  .refine(
    (data) =>
      !data.existingFieldIds ||
      !data.existingFieldIds.includes(data.id) ||
      data.id === data.currentFieldId,
    {
      path: ['id'],
      message: 'ID must be unique',
    }
  )
  .refine(
    (data) =>
      !data.options ||
      data.options.length <= FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT,
    {
      path: ['options'],
      message: `Maximum ${FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT} options allowed`,
    }
  );

export const floatDataFieldSchema = baseDataFieldSchema
  .extend({
    type: z.literal('float'),
    default_value: z.number().nullable().optional(),
    options: z.array(z.number()).nullable().optional(),
  })
  .refine(
    (data) =>
      !data.existingFieldIds ||
      !data.existingFieldIds.includes(data.id) ||
      data.id === data.currentFieldId,
    {
      path: ['id'],
      message: 'ID must be unique',
    }
  )
  .refine(
    (data) =>
      !data.options ||
      data.options.length <= FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT,
    {
      path: ['options'],
      message: `Maximum ${FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT} options allowed`,
    }
  );

export const booleanDataFieldSchema = baseDataFieldSchema
  .extend({
    type: z.literal('boolean'),
    default_value: z.boolean().nullable().optional(),
    options: z.array(z.boolean()).nullable().optional(),
  })
  .refine(
    (data) =>
      !data.existingFieldIds ||
      !data.existingFieldIds.includes(data.id) ||
      data.id === data.currentFieldId,
    {
      path: ['id'],
      message: 'ID must be unique',
    }
  )
  .refine(
    (data) =>
      !data.options ||
      data.options.length <= FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT,
    {
      path: ['options'],
      message: `Maximum ${FIELD_VALIDATION_CONSTANTS.MAX_OPTIONS_COUNT} options allowed`,
    }
  );

export type DataFieldInputValidation =
  | z.infer<typeof stringDataFieldSchema>
  | z.infer<typeof integerDataFieldSchema>
  | z.infer<typeof floatDataFieldSchema>
  | z.infer<typeof booleanDataFieldSchema>;
